import { Delaunay } from 'd3';
import type { NumericRange } from '../types';
import type {
  AxisCounts,
  CoincidentAggregation,
  GridResolution,
  ScalarGrid,
  XyzDataPoint,
  ZInterpolation,
} from '../types';
import { maxOf, minOf } from './ranges';
import { collapseCoincident, type CollapsedSample } from './collapseCoincident';
import { toExtentOfSamples, type SampledExtent } from './horizontalAxes';

export type GriddingArgs = {
  points: XyzDataPoint[];
  interpolation: ZInterpolation;
  /** How several components on one position reduce to a single z. */
  aggregation: CoincidentAggregation;
  /**
   * Samples along each axis, or bins along each axis under `binned` - one count for both axes, or
   * one each. Cost is the product of the two, so it is a real knob.
   */
  resolution: GridResolution;
  /**
   * Grow the filled region past the triangulation's boundary, for a caller that clips it back.
   *
   * A cell is filled when a triangle claims its *centre*, so the filled region stops up to half a
   * cell short of the boundary the samples actually describe. That shortfall is the stair-stepped
   * edge, and it is why a sample can sit outside the color while being a vertex of the very
   * triangulation that produced it - the sample is inside the hull, its cell's centre is not.
   *
   * Growing and then clipping to `hull` is what turns that into a straight edge. Either half alone
   * does nothing: clipping without growing removes a fringe that was never drawn, and growing
   * without clipping paints past the data. Off by default, because the 3D surface builds a mesh
   * from these cells and has nothing to clip with - it would simply gain a skirt of invented
   * geometry.
   */
  overfillForClipping?: boolean;
  /**
   * The domain to sample over, for a second field that has to share another grid's lattice. Taken
   * from the samples when absent.
   */
  extent?: SampledExtent;
};

/** Barycentric coordinates are compared against this rather than 0, so a cell sitting
 *  exactly on a shared triangle edge is claimed rather than dropped as a seam. */
const EDGE_TOLERANCE = 1e-9;

const IDW_POWER = 2;

/** Guards the weight at a cell that coincides with a sample, where distance is 0. */
const IDW_EPSILON = 1e-12;

const getRange = (values: number[]): NumericRange => [
  minOf(values),
  maxOf(values),
];

/**
 * Pads a zero-width range so a single distinct value still produces a drawable extent
 * rather than a division by zero downstream.
 */
const padDegenerate = ([min, max]: NumericRange): NumericRange =>
  min === max ? [min - 0.5, max + 0.5] : [min, max];

const toGridCoordinate = (
  value: number,
  [min, max]: NumericRange,
  samples: number
): number => ((value - min) / (max - min)) * (samples - 1);

const toWorldCoordinate = (
  index: number,
  [min, max]: NumericRange,
  samples: number
): number => min + (index / (samples - 1)) * (max - min);

/**
 * Rasterises the Delaunay triangulation into the grid, interpolating barycentrically
 * inside each triangle and leaving everything outside the convex hull null.
 *
 * Driven per triangle rather than per cell: each triangle touches its own bounding box
 * and nothing else, so the whole grid is filled in one pass over the triangles instead
 * of a containment search per cell. Cells no triangle claims stay null, which is how the
 * hull boundary falls out for free rather than being computed and clipped against.
 */
const fillLinear = (
  values: (number | null)[],
  samples: CollapsedSample[],
  xRange: NumericRange,
  yRange: NumericRange,
  { x: columns, y: rows }: AxisCounts,
  delaunay: Delaunay<CollapsedSample>
): void => {
  const { triangles } = delaunay;

  for (let t = 0; t < triangles.length; t += 3) {
    const a = samples[triangles[t]];
    const b = samples[triangles[t + 1]];
    const c = samples[triangles[t + 2]];

    const ax = toGridCoordinate(a.x, xRange, columns);
    const ay = toGridCoordinate(a.y, yRange, rows);
    const bx = toGridCoordinate(b.x, xRange, columns);
    const by = toGridCoordinate(b.y, yRange, rows);
    const cx = toGridCoordinate(c.x, xRange, columns);
    const cy = toGridCoordinate(c.y, yRange, rows);

    const denominator = (by - cy) * (ax - cx) + (cx - bx) * (ay - cy);
    /* A zero denominator is a zero-area triangle: collinear samples that survived the
       collapse. It contributes no area to fill, so there is nothing to interpolate. */
    if (denominator === 0) continue;

    const minColumn = Math.max(0, Math.ceil(Math.min(ax, bx, cx)));
    const maxColumn = Math.min(columns - 1, Math.floor(Math.max(ax, bx, cx)));
    const minRow = Math.max(0, Math.ceil(Math.min(ay, by, cy)));
    const maxRow = Math.min(rows - 1, Math.floor(Math.max(ay, by, cy)));

    for (let row = minRow; row <= maxRow; row++) {
      for (let column = minColumn; column <= maxColumn; column++) {
        const weightA =
          ((by - cy) * (column - cx) + (cx - bx) * (row - cy)) / denominator;
        const weightB =
          ((cy - ay) * (column - cx) + (ax - cx) * (row - cy)) / denominator;
        const weightC = 1 - weightA - weightB;

        if (
          weightA < -EDGE_TOLERANCE ||
          weightB < -EDGE_TOLERANCE ||
          weightC < -EDGE_TOLERANCE
        ) {
          continue;
        }

        values[row * columns + column] =
          weightA * a.z + weightB * b.z + weightC * c.z;
      }
    }
  }
};

/**
 * Shepard inverse-distance weighting over every sample.
 *
 * Defined across the whole grid, which is its appeal and its cost: it will happily
 * report a height in a region where nothing was measured. Left as an opt-in for that
 * reason.
 */
/** A position the smooth fill pulls towards, and how many samples are speaking through it. */
type IdwSource = { x: number; y: number; z: number; strength: number };

/**
 * How many distance calculations the smooth fill is allowed in total.
 *
 * Shepard weighting is `cells x sources`, and nothing else bounds the second factor:
 * `collapseCoincident` is what keeps a 1-5 score estate cheap, and it collapses nothing at all
 * when x and y are continuous - a cost, a headcount, a date serial - which is exactly where
 * "defined everywhere" is most attractive. Measured unbounded at resolution 64: 500 samples 32 ms,
 * 2,000 98 ms, 20,000 1.3 s, linear from there, so a large estate freezes the tab rather than
 * merely being slow. `linear` has no such shape; it walks each triangle's own bounding box.
 *
 * Bounding the product rather than the sample count is what keeps the detail dial from undoing
 * it: at 64 the budget allows 4,096 sources, at 128 it allows 1,024.
 */
const MAXIMUM_IDW_DISTANCE_CALCULATIONS = 16_777_216;

/** Never summarise below this, or a coarse chart would be reduced to a handful of blobs. */
const MINIMUM_IDW_SOURCES = 256;

/**
 * The samples, or a binned summary of them once there are too many to weigh one by one.
 *
 * Each bin stands at the mean position of what it holds and pulls with their combined strength,
 * so a dense region still dominates its neighbourhood. That is an approximation, but a small one
 * against a method that is already a smoothing estimate - and the alternative is not an exact
 * answer, it is an unresponsive tab.
 */
const toIdwSources = (
  samples: CollapsedSample[],
  xRange: NumericRange,
  yRange: NumericRange,
  { x: columns, y: rows }: AxisCounts
): IdwSource[] => {
  const budget = Math.max(
    MINIMUM_IDW_SOURCES,
    Math.floor(MAXIMUM_IDW_DISTANCE_CALCULATIONS / (columns * rows))
  );
  if (samples.length <= budget) {
    return samples.map(({ x, y, z }) => ({ x, y, z, strength: 1 }));
  }

  /* Binned at the output lattice where the budget allows it, so a source sits at most half a cell
     from what it summarises - the fill cannot resolve detail finer than the lattice it is filling,
     so summarising at that same lattice loses nothing the output could have shown. */
  const side = Math.min(Math.max(columns, rows), Math.floor(Math.sqrt(budget)));
  const bins = new Map<number, IdwSource>();
  const binIndex = (value: number, [min, max]: NumericRange): number =>
    Math.min(
      side - 1,
      Math.max(0, Math.floor(((value - min) / (max - min)) * side))
    );

  for (const { x, y, z } of samples) {
    const key = binIndex(y, yRange) * side + binIndex(x, xRange);
    const bin = bins.get(key);
    if (bin) {
      bin.x += x;
      bin.y += y;
      bin.z += z;
      bin.strength += 1;
      continue;
    }
    bins.set(key, { x, y, z, strength: 1 });
  }

  return Array.from(bins.values(), ({ x, y, z, strength }) => ({
    x: x / strength,
    y: y / strength,
    z: z / strength,
    strength,
  }));
};

const fillSmooth = (
  values: (number | null)[],
  samples: CollapsedSample[],
  xRange: NumericRange,
  yRange: NumericRange,
  counts: AxisCounts
): void => {
  const sources = toIdwSources(samples, xRange, yRange, counts);
  const { x: columns, y: rows } = counts;

  for (let row = 0; row < rows; row++) {
    const worldY = toWorldCoordinate(row, yRange, rows);
    for (let column = 0; column < columns; column++) {
      const worldX = toWorldCoordinate(column, xRange, columns);

      let weightedSum = 0;
      let weightTotal = 0;
      for (const source of sources) {
        const dx = worldX - source.x;
        const dy = worldY - source.y;
        const weight =
          source.strength /
          (Math.pow(dx * dx + dy * dy, IDW_POWER / 2) + IDW_EPSILON);
        weightedSum += weight * source.z;
        weightTotal += weight;
      }

      values[row * columns + column] = weightedSum / weightTotal;
    }
  }
};

export type BinIndexArgs = {
  sample: { x: number; y: number };
  xRange: NumericRange;
  yRange: NumericRange;
  /** Bins along each axis. */
  counts: AxisCounts;
};

/** The lattice index of the bin a position falls in under `binned`, or null outside the lattice. */
export const toBinIndex = ({
  sample,
  xRange,
  yRange,
  counts: { x: columns, y: rows },
}: BinIndexArgs): number | null => {
  const column = Math.round(toGridCoordinate(sample.x, xRange, columns));
  const row = Math.round(toGridCoordinate(sample.y, yRange, rows));
  if (column < 0 || column >= columns || row < 0 || row >= rows) {
    return null;
  }
  return row * columns + column;
};

/**
 * Aggregates the samples falling inside each cell and leaves empty cells null.
 *
 * The only kernel that interpolates nothing, so the only one whose every value is
 * traceable to components that are actually there.
 */
const fillBinned = (
  values: (number | null)[],
  samples: CollapsedSample[],
  xRange: NumericRange,
  yRange: NumericRange,
  counts: AxisCounts,
  aggregation: CoincidentAggregation
): void => {
  /** Each position's contribution to its cell, and how many components stand behind it. */
  const perCell = new Map<number, { values: number[]; counts: number[] }>();

  for (const sample of samples) {
    const index = toBinIndex({ sample, xRange, yRange, counts });
    if (index === null) continue;
    const existing = perCell.get(index);
    if (existing) {
      existing.values.push(sample.z);
      existing.counts.push(sample.count);
      continue;
    }
    perCell.set(index, { values: [sample.z], counts: [sample.count] });
  }

  for (const [index, { values: cellValues, counts }] of perCell) {
    if (aggregation === 'sum') {
      values[index] = cellValues.reduce((sum, value) => sum + value, 0);
      continue;
    }
    if (aggregation === 'max') {
      values[index] = maxOf(cellValues);
      continue;
    }
    if (aggregation === 'min') {
      values[index] = minOf(cellValues);
      continue;
    }
    /* Weighted, because this aggregation runs twice over the same operator with different
       populations: `collapseCoincident` already reduced each position to a mean over the
       components there, and averaging those unweighted would count a position of forty the same
       as one of one. The other operators compose without this - summing sums is the sum, the max
       of maxes is the max - which is why only this branch carries the counts. */
    const weighted = cellValues.reduce(
      (sum, value, at) => sum + value * counts[at],
      0
    );
    const total = counts.reduce((sum, count) => sum + count, 0);
    values[index] = weighted / total;
  }
};

/**
 * Copies each filled cell one step into its empty neighbours, twice.
 *
 * Four passes because the shortfall is up to half a cell on a straight edge and more where the boundary
 * runs diagonally across the lattice. Overshooting costs nothing: the caller clips to the hull, and
 * anything grown past it is discarded.
 *
 * Reads from a snapshot so a pass cannot feed itself and run away across the whole grid.
 */
const overfill = (
  values: (number | null)[],
  { x: columns, y: rows }: AxisCounts,
  passes = 4
): void => {
  for (let pass = 0; pass < passes; pass++) {
    const before = [...values];
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < columns; x++) {
        const index = y * columns + x;
        if (before[index] !== null) continue;

        const neighbours = [
          x > 0 ? before[index - 1] : null,
          x < columns - 1 ? before[index + 1] : null,
          y > 0 ? before[index - columns] : null,
          y < rows - 1 ? before[index + columns] : null,
        ].filter((value): value is number => value !== null);

        if (neighbours.length > 0) {
          values[index] =
            neighbours.reduce((sum, value) => sum + value, 0) /
            neighbours.length;
        }
      }
    }
  }
};

const EMPTY_GRID: ScalarGrid = {
  values: [],
  width: 0,
  height: 0,
  xRange: [0, 1],
  yRange: [0, 1],
  dataXRange: [0, 1],
  dataYRange: [0, 1],
  zRange: [0, 1],
  dataZRange: [0, 1],
  hull: [],
};

/** The per-axis counts a resolution stands for. */
const toAxisCounts = (resolution: GridResolution): AxisCounts =>
  typeof resolution === 'number'
    ? { x: resolution, y: resolution }
    : resolution;

/** Whether two resolutions grid at the same counts, however each is written. */
export const isSameResolution = (
  a: GridResolution,
  b: GridResolution
): boolean => {
  const first = toAxisCounts(a);
  const second = toAxisCounts(b);
  return first.x === second.x && first.y === second.y;
};

/** The samples' own outline, which is the boundary the lattice only approximates. */
const toHull = (
  delaunay: Delaunay<CollapsedSample>,
  samples: CollapsedSample[]
): [number, number][] =>
  Array.from(delaunay.hull, index => [samples[index].x, samples[index].y]);

/**
 * Turns scattered components into the lattice a surface or contour needs.
 *
 * Returns an empty grid rather than guessing when there is nothing to interpolate over -
 * fewer than three positions, or positions that are all collinear, give a triangulation
 * with no area. Falling back to a smoother kernel there would draw a confident surface
 * out of data that cannot support one, so the caller gets an empty grid and can say so.
 */
export const toScalarGrid = ({
  points,
  interpolation,
  aggregation,
  resolution,
  overfillForClipping = false,
  extent,
}: GriddingArgs): ScalarGrid => {
  const samples = collapseCoincident(points, aggregation);
  if (samples.length === 0) return EMPTY_GRID;

  /* Through the shared implementation, because the host derives the axes from the same extent
     and the two have to agree to the last bit. See `toExtentOfSamples`. */
  const { dataXRange: xRange, dataYRange: yRange } =
    extent ?? toExtentOfSamples(samples);
  const counts = toAxisCounts(resolution);
  const values: (number | null)[] = new Array(counts.x * counts.y).fill(null);

  /* Built here rather than inside `fillLinear` so the hull is available whichever kernel runs. The
     boundary is a fact about the samples, not about how they were interpolated. */
  const delaunay = Delaunay.from(
    samples,
    ({ x }) => x,
    ({ y }) => y
  );

  if (interpolation === 'binned') {
    fillBinned(values, samples, xRange, yRange, counts, aggregation);
  } else if (interpolation === 'smooth') {
    fillSmooth(values, samples, xRange, yRange, counts);
  } else {
    fillLinear(values, samples, xRange, yRange, counts, delaunay);
  }

  /* Only where a boundary exists to overshoot. `binned` leaves deliberate gaps between occupied
     cells and growing them would join bins that have nothing between them; `smooth` already
     reaches everywhere its kernel does. */
  if (overfillForClipping && interpolation === 'linear') {
    overfill(values, counts);
  }

  const filled = values.filter((value): value is number => value !== null);
  if (filled.length === 0) return EMPTY_GRID;

  const dataZRange = padDegenerate(getRange(filled));

  return {
    values,
    width: counts.x,
    height: counts.y,
    /* Each range under two names, because gridding has no opinion about axes. The caller owns
       `xRange`, `yRange` and `zRange`, and leaves the three `data*` ranges alone. */
    xRange,
    yRange,
    dataXRange: xRange,
    dataYRange: yRange,
    zRange: dataZRange,
    dataZRange,
    hull: toHull(delaunay, samples),
  };
};
